import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Settings, CreditCard, Menu, LayoutDashboard, Brain, BarChart3, BookOpen, Users, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthSession } from '@/hooks/useAuthSession';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface NavbarProps {
  mobileMenuOpen?: boolean;
  setMobileMenuOpen?: (open: boolean) => void;
  showMobileMenuButton?: boolean;
}

const navigationItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Quiz', href: '/quiz', icon: Brain },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Flashcards', href: '/flashcards', icon: BookOpen },
  { name: 'Community', href: '/community', icon: Users },
  { name: 'Support', href: '/support', icon: MessageSquare },
];

const Navbar = ({ mobileMenuOpen, setMobileMenuOpen, showMobileMenuButton }: NavbarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { isAuthenticated, session } = useAuthSession();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSignOut = async () => {
    if (isSigningOut) return;
    try {
      setIsSigningOut(true);
      await signOut();
    } catch (error) {
      console.error("Error in Navbar handleSignOut:", error);
      toast.error("Error signing out. Please try again.");
    } finally {
      setTimeout(() => setIsSigningOut(false), 2000);
    }
  };

  const getUserInitials = () => {
    if (!session?.user?.email) return "U";
    const userMeta = session.user.user_metadata;
    const firstName = userMeta?.first_name || '';
    const lastName = userMeta?.last_name || '';
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (firstName) return firstName[0].toUpperCase();
    const email = session.user.email;
    const nameMatch = email.match(/^([^@])/);
    return nameMatch ? nameMatch[1].toUpperCase() : "U";
  };

  return (
    <nav className="sticky top-0 z-40 w-full border-b bg-white/80 backdrop-blur-md shadow-md">
      <div className="container flex h-16 items-center pl-4 pr-6 sm:pr-8 lg:px-8 transition-all duration-300">
        {showMobileMenuButton && (
          <Button variant="ghost" size="icon" className="mr-2 md:hidden" onClick={() => setMobileMenuOpen?.(true)}>
            <Menu className="h-5 w-5" />
            <span className="sr-only">Open mobile menu</span>
          </Button>
        )}

        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 relative">
            <img src="/lovable-uploads/8dd4efad-6e3c-4413-9e65-4c7880387263.png" alt="Edaptica Logo" className="w-full h-full object-contain" />
          </div>
          <span className="text-xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradientShift">
            Edaptica
          </span>
        </Link>

        <div className="ml-auto flex items-center space-x-2">
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2 rounded-full p-1 pr-4 hover:bg-accent hover:text-accent-foreground bg-primary-50/30">
                  <Avatar className="h-8 w-8 border-2 border-primary/20 hover:ring-2 hover:ring-primary">
                    <AvatarImage src={session?.user.user_metadata?.avatar_url || ''} alt={session?.user.email || 'User'} />
                    <AvatarFallback className="bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-700 font-medium">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium hidden sm:inline-block">
                    {session?.user.email?.split('@')[0] || 'User'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 animate-in fade-in slide-in-from-top-1">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/subscription/plans')}>
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>Subscription</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleSignOut} 
                  className={cn("text-red-600 focus:text-red-700", isSigningOut && "opacity-50 cursor-not-allowed")}
                  disabled={isSigningOut}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{isSigningOut ? "Logging out..." : "Log out"}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="default" asChild className="bg-gradient-to-r from-primary to-secondary hover:from-primary-600 hover:to-secondary-600 text-white">
              <Link to="/auth">Sign in</Link>
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
